import { IconX } from '@tabler/icons-react';
import { useRef, useState, useEffect, useCallback } from 'react';
import { getAuthorizationHeader, http } from '../../config/http';
import CetakStroke from './CetakStroke';
import { toast } from 'react-toastify';
import soundSuccess from '../../assets/sound.mp3';
import { CurrencyInput } from '../../utils/CurrencyInput';
import TestingPrint from './TestingPrint';
import SuratSemuaToko from '../../components/surat/SuratSemuaToko';
import PrintSuratTrxPenjualan from '../Transaksi/PrintSuratTrxPenjualan';
import SuratKeruak from '../Transaksi/SuratKeruak';
import SuratToko from '../../components/surat/SuratToko';
import { PhotoView, PhotoProvider } from 'react-photo-view';
import { imageBaseUrl } from '../../config/http';
import { useMasterData } from '../../context/MasterDataContext';
import ModalKonfirmasiPenjualan from './ModalKonfirmasiPenjualan';
import { terbilang } from '../../utils/number';
import BankLogo from '../../components/BankLogo';

export default function DetailBarangPenjualan({ itemCabang, detailPenjualan, isTertunda, loading, kodeItem, onReset }) {
  const { bankList: masterBankList, paymentMethods } = useMasterData();
  const bankList = masterBankList && masterBankList.length > 0 ? masterBankList : ['BCA', 'BNI', 'BRI', 'Mandiri', 'BSI', 'Lainnya'];
  const defaultBank = bankList[0] || 'BCA';

  // Daftar metode pembayaran yang aktif
  const availableMethodIds = ['cash', 'transfer', 'qris', 'edc'].filter(
    (m) => paymentMethods?.[m] !== false
  );
  const firstAvailableMethod = availableMethodIds[0] || 'cash';

  const activeItem = Array.isArray(itemCabang)
    ? (itemCabang.length > 0 ? itemCabang[0] : null)
    : (itemCabang || null);
  const item_id = activeItem?.id;
  const hasValidItem = Boolean(activeItem?.id || activeItem?.kode_item);

  const getEffectiveBankList = (currentBank) => {
    if (currentBank && !bankList.includes(currentBank)) {
      return [currentBank, ...bankList];
    }
    return bankList;
  };

  const [hargaPenjualan, setHarga] = useState('');
  const [msg] = useState('');
  const [btnLoading, setBtnLoading] = useState(false);
  const [harga_jual, setHargaJual] = useState('');
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  // Mode Pembayaran: 'single' (1 Metode Penuh 100%) vs 'split' (Bagi Pembayaran)
  const [paymentMode, setPaymentMode] = useState('single');
  const [singleMethod, setSingleMethod] = useState(firstAvailableMethod); // 'cash' | 'qris' | 'transfer' | 'edc'
  const [singleBank, setSingleBank] = useState(defaultBank);

  const [splitMethods, setSplitMethods] = useState([
    { id: 'cash', label: 'Cash', nama_bank: '', nominal: '' },
    { id: 'transfer', label: 'Transfer', nama_bank: defaultBank, nominal: '' },
    { id: 'qris', label: 'QRIS', nama_bank: '', nominal: '' },
    { id: 'edc', label: 'EDC', nama_bank: defaultBank, nominal: '' },
  ]);

  const [selectImage, setSelectImage] = useState([]);
  const [gambar, setGambar] = useState('');
  const [namaPembeli, setNamaPembeli] = useState('');
  const [noHp, setNoHp] = useState('');

  // Sinkronisasi jika metode pembayaran yang dipilih saat ini dinonaktifkan dari pengaturan
  useEffect(() => {
    if (availableMethodIds.length > 0 && !availableMethodIds.includes(singleMethod)) {
      setSingleMethod(availableMethodIds[0]);
    }
    if (availableMethodIds.length <= 1 && paymentMode === 'split') {
      setPaymentMode('single');
    }
  }, [availableMethodIds, singleMethod, paymentMode]);

  // Reset semua inputan ke default empty
  const resetAllInputs = useCallback(() => {
    setHarga('');
    setHargaJual('');
    setNamaPembeli('');
    setNoHp('');
    setPaymentMode('single');
    setSingleMethod(firstAvailableMethod);
    setSingleBank(defaultBank);
    setSplitMethods([
      { id: 'cash', label: 'Cash', nama_bank: '', nominal: '' },
      { id: 'transfer', label: 'Transfer', nama_bank: defaultBank, nominal: '' },
      { id: 'qris', label: 'QRIS', nama_bank: '', nominal: '' },
      { id: 'edc', label: 'EDC', nama_bank: defaultBank, nominal: '' },
    ]);
    setSelectImage([]);
    setGambar('');
    const fileInput = document.querySelector('input[type="file"]');
    if (fileInput) fileInput.value = '';
  }, [defaultBank, firstAvailableMethod]);

  // Sinkronisasi bank default jika master data bank baru selesai dimuat
  useEffect(() => {
    if (defaultBank) {
      setSingleBank((prev) => prev || defaultBank);
      setSplitMethods((prev) =>
        prev.map((m) => {
          if ((m.id === 'transfer' || m.id === 'edc') && !m.nama_bank) {
            return { ...m, nama_bank: defaultBank };
          }
          return m;
        })
      );
    }
  }, [defaultBank]);

  const currentItemId = activeItem?.id;
  const prevItemIdRef = useRef(currentItemId);

  // Reset semua inputan ketika kodeItem di-reset/dihapus atau berganti item
  useEffect(() => {
    if (!kodeItem || !kodeItem.trim() || !hasValidItem) {
      resetAllInputs();
    } else if (prevItemIdRef.current && prevItemIdRef.current !== currentItemId) {
      resetAllInputs();
    }
    prevItemIdRef.current = currentItemId;
  }, [kodeItem, hasValidItem, currentItemId, resetAllInputs]);

  const [dataPrint, setDataPrint] = useState([]);
  let [isOpen, setIsOpen] = useState(false);

  const hargaTotalRef = useRef(null);

  useEffect(() => {
    // Ketika pencarian item ditemukan di halaman kasir, otomatis fokus kursor ke input harga total
    if (hasValidItem) {
      const timer = setTimeout(() => {
        const inputEl = hargaTotalRef.current || document.getElementById('input-harga-total');
        if (inputEl) {
          inputEl.focus();
          inputEl.select?.();
        }
      }, 80);
      return () => clearTimeout(timer);
    }
  }, [hasValidItem, currentItemId]);

  function playSound() {
    new Audio(soundSuccess).play();
  }

  const handleChange = (newValue) => {
    let val = newValue === undefined ? '' : newValue;
    setHarga(val);
    setHargaJual(val);

    // Jika sedang dalam mode split, otomatis sesuaikan nominal cash dengan harga jual baru
    if (paymentMode === 'split') {
      const newPrice = parseInt(val) || 0;
      setSplitMethods((prev) => {
        const nonCashTotal = prev.reduce((sum, p) => {
          return p.id !== 'cash' ? sum + (parseInt(p.nominal) || 0) : sum;
        }, 0);
        const remainingForCash = newPrice - nonCashTotal;
        const newCash = remainingForCash > 0 ? remainingForCash : (nonCashTotal > 0 ? 0 : newPrice);

        return prev.map((p) => {
          if (p.id === 'cash') {
            return {
              ...p,
              nominal: newCash > 0 ? newCash : (newPrice > 0 && nonCashTotal >= newPrice ? 0 : '')
            };
          }
          return p;
        });
      });
    }
  };

  function numberWithCommas(x) {
    if (x === null || x === undefined || x === '') return '0';
    return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function checkBerat(number) {
    if (number === null || number === undefined || number === '') return '0';
    const beratConfirm = parseFloat(number);
    return beratConfirm % 1 === 0
      ? beratConfirm.toString()
      : beratConfirm
          .toFixed(2)
          .replace(/\.?0+$/, '')
          .replace('.', ',');
  }

  const strukComponent = useRef();

  function closeModal() {
    setIsOpen(false);
    setDataPrint([]);
    resetAllInputs();
    if (onReset) {
      onReset();
    }
  }

  // Beralih ke Mode Split - Default otomatis terisi: penyeimbang = total bayarnya
  const switchToSplit = useCallback(() => {
    const currentPrice = parseInt(harga_jual) || 0;
    const balanceMethodId = paymentMethods?.cash !== false ? 'cash' : (availableMethodIds[0] || 'cash');

    setSplitMethods((prev) => {
      const otherTotal = prev.reduce((sum, m) => {
        if (m.id !== balanceMethodId && paymentMethods?.[m.id] !== false) {
          return sum + (parseInt(m.nominal) || 0);
        }
        return sum;
      }, 0);

      // Hitung sisa untuk balance method (jika belum ada metode lain terisi, balance = full currentPrice)
      const remainingForBalance = currentPrice - otherTotal;
      const balanceVal = remainingForBalance > 0 ? remainingForBalance : (remainingForBalance === 0 && otherTotal > 0 ? 0 : currentPrice);

      return prev.map((m) => {
        if (m.id === balanceMethodId) {
          return { ...m, nominal: balanceVal > 0 ? balanceVal : (currentPrice > 0 ? 0 : '') };
        }
        return m;
      });
    });
    setPaymentMode('split');
  }, [harga_jual, paymentMethods, availableMethodIds]);

  // Beralih ke Mode Single
  const switchToSingle = useCallback((methodId) => {
    const target = methodId || singleMethod || firstAvailableMethod;
    if (paymentMethods?.[target] !== false) {
      setSingleMethod(target);
    } else {
      setSingleMethod(firstAvailableMethod);
    }
    setPaymentMode('single');
  }, [singleMethod, paymentMethods, firstAvailableMethod]);

  // Handler Split: Setiap ada perubahan (nambah/kurang) pada non-balance method,
  // otomatis sesuaikan nominal balanceMethod (default CASH) sebagai penyeimbang
  const handleSplitChange = (methodId, field, value) => {
    const targetPrice = parseInt(harga_jual) || 0;
    const balanceMethodId = paymentMethods?.cash !== false ? 'cash' : (availableMethodIds.find((id) => id !== methodId) || 'cash');

    if (field === 'nominal' && methodId !== balanceMethodId) {
      const parsedVal = (value === '' || value === undefined) ? 0 : (parseInt(value) || 0);

      setSplitMethods((prev) => {
        // Total metode lain selain balanceMethodId dan methodId yang sedang diedit
        const otherTotal = prev.reduce((sum, p) => {
          if (p.id !== balanceMethodId && p.id !== methodId && paymentMethods?.[p.id] !== false) {
            return sum + (parseInt(p.nominal) || 0);
          }
          return sum;
        }, 0);

        const totalNonBalance = otherTotal + parsedVal;
        const remainingForBalance = targetPrice - totalNonBalance;
        const newBalance = remainingForBalance > 0 ? remainingForBalance : 0;

        return prev.map((p) => {
          if (p.id === methodId) {
            return { ...p, nominal: value };
          }
          if (p.id === balanceMethodId) {
            return {
              ...p,
              nominal: newBalance > 0 ? newBalance : (totalNonBalance >= targetPrice ? 0 : '')
            };
          }
          return p;
        });
      });
    } else {
      // Jika user mengedit input balanceMethodId secara manual atau mengganti nama_bank
      setSplitMethods((prev) =>
        prev.map((p) => (p.id === methodId ? { ...p, [field]: value } : p))
      );
    }
  };

  const handleFillRemaining = (methodId) => {
    const targetPrice = parseInt(harga_jual) || 0;
    const balanceMethodId = paymentMethods?.cash !== false ? 'cash' : (availableMethodIds.find((id) => id !== methodId) || 'cash');

    if (methodId !== balanceMethodId) {
      const otherTotal = splitMethods.reduce((sum, p) => {
        if (p.id !== balanceMethodId && p.id !== methodId && paymentMethods?.[p.id] !== false) {
          return sum + (parseInt(p.nominal) || 0);
        }
        return sum;
      }, 0);

      const remaining = targetPrice - otherTotal;
      const newNominal = remaining > 0 ? remaining : 0;

      setSplitMethods((prev) =>
        prev.map((p) => {
          if (p.id === methodId) {
            const bank = (p.id === 'transfer' || p.id === 'edc') ? (p.nama_bank || defaultBank) : p.nama_bank;
            return { ...p, nominal: newNominal, nama_bank: bank };
          }
          if (p.id === balanceMethodId) {
            return { ...p, nominal: 0 };
          }
          return p;
        })
      );
    } else {
      const otherTotal = splitMethods.reduce((sum, p) => {
        return (p.id !== balanceMethodId && paymentMethods?.[p.id] !== false) ? sum + (parseInt(p.nominal) || 0) : sum;
      }, 0);
      const remaining = targetPrice - otherTotal;
      const newBalance = remaining > 0 ? remaining : 0;

      setSplitMethods((prev) =>
        prev.map((p) => (p.id === balanceMethodId ? { ...p, nominal: newBalance } : p))
      );
    }
  };

  const handleResetSplit = (methodId) => {
    const targetPrice = parseInt(harga_jual) || 0;
    const balanceMethodId = paymentMethods?.cash !== false ? 'cash' : (availableMethodIds.find((id) => id !== methodId) || 'cash');

    if (methodId !== balanceMethodId) {
      setSplitMethods((prev) => {
        const remainingOtherTotal = prev.reduce((sum, p) => {
          if (p.id !== balanceMethodId && p.id !== methodId && paymentMethods?.[p.id] !== false) {
            return sum + (parseInt(p.nominal) || 0);
          }
          return sum;
        }, 0);

        const remainingForBalance = targetPrice - remainingOtherTotal;
        const newBalance = remainingForBalance > 0 ? remainingForBalance : 0;

        return prev.map((p) => {
          if (p.id === methodId) {
            return { ...p, nominal: '' };
          }
          if (p.id === balanceMethodId) {
            return {
              ...p,
              nominal: newBalance > 0 ? newBalance : (remainingOtherTotal >= targetPrice ? 0 : '')
            };
          }
          return p;
        });
      });
    } else {
      setSplitMethods((prev) =>
        prev.map((p) => (p.id === balanceMethodId ? { ...p, nominal: '' } : p))
      );
    }
  };

  // Shortcut Keyboard: Alt+1 (Cash), Alt+2 (QRIS), Alt+3 (Transfer), Alt+4 (EDC), Alt+S (Toggle Split)
  useEffect(() => {
    const handleKeyShortcut = (e) => {
      if (e.altKey) {
        if (e.key === '1' && paymentMethods?.cash !== false) {
          e.preventDefault();
          switchToSingle('cash');
        } else if (e.key === '2' && paymentMethods?.qris !== false) {
          e.preventDefault();
          switchToSingle('qris');
        } else if (e.key === '3' && paymentMethods?.transfer !== false) {
          e.preventDefault();
          switchToSingle('transfer');
        } else if (e.key === '4' && paymentMethods?.edc !== false) {
          e.preventDefault();
          switchToSingle('edc');
        } else if (e.key?.toLowerCase() === 's' && availableMethodIds.length > 1) {
          e.preventDefault();
          setPaymentMode((prev) => (prev === 'single' ? 'split' : 'single'));
        }
      }
    };
    window.addEventListener('keydown', handleKeyShortcut);
    return () => window.removeEventListener('keydown', handleKeyShortcut);
  }, [switchToSingle, paymentMethods, availableMethodIds.length]);

  // Kalkulasi Status Pembayaran
  const nominalHargaJual = parseInt(harga_jual) || 0;
  const totalSplit = splitMethods
    .filter((p) => paymentMethods?.[p.id] !== false)
    .reduce((sum, p) => sum + (parseInt(p.nominal) || 0), 0);
  const sisaSplit = nominalHargaJual - totalSplit;

  // Active Payments yang akan dikirim ke backend
  const activePayments = paymentMode === 'single'
    ? [
        {
          metode_pembayaran: singleMethod,
          nama_bank: (singleMethod === 'transfer' || singleMethod === 'edc') ? singleBank : '',
          nominal: nominalHargaJual,
        },
      ]
    : splitMethods
        .filter((p) => paymentMethods?.[p.id] !== false && (parseInt(p.nominal) || 0) > 0)
        .map((p) => ({
          metode_pembayaran: p.id,
          nama_bank: (p.id === 'transfer' || p.id === 'edc') ? (p.nama_bank || defaultBank) : '',
          nominal: parseInt(p.nominal) || 0,
        }));

  const totalPembayaranSaatIni = activePayments.reduce((sum, p) => sum + (parseInt(p.nominal) || 0), 0);

  // Buka Modal Konfirmasi Sebelum Submit
  const handlePreSubmit = (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!hasValidItem) {
      toast.warning('Pilih / scan barang terlebih dahulu');
      return;
    }
    if (nominalHargaJual <= 0) {
      toast.warning('Masukkan nominal harga penjualan terlebih dahulu');
      return;
    }

    setIsConfirmOpen(true);
  };

  // Eksekusi Submit Final dari Modal Konfirmasi
  const handleConfirmSubmit = async () => {
    if (nominalHargaJual <= 0 || !hasValidItem) return;

    if (totalPembayaranSaatIni !== nominalHargaJual) {
      toast.error(
        `Total pembayaran (Rp ${numberWithCommas(totalPembayaranSaatIni)}) belum sesuai dengan harga jual (Rp ${numberWithCommas(nominalHargaJual)})`,
        { autoClose: 3000 }
      );
      return;
    }

    if (paymentMode === 'split' && sisaSplit !== 0) {
      toast.error(
        `Total split (Rp ${numberWithCommas(totalSplit)}) belum sesuai dengan total harga (Rp ${numberWithCommas(nominalHargaJual)})`,
        { autoClose: 3000 }
      );
      return;
    }

    if (paymentMode === 'single' && (singleMethod === 'transfer' || singleMethod === 'edc') && !singleBank) {
      toast.error('Pilih rekening / mesin bank terlebih dahulu');
      return;
    }

    setBtnLoading(true);

    const data = {
      harga_jual: nominalHargaJual,
      item_id,
      gambar,
      payments: activePayments,
      nama_pembeli: namaPembeli.trim() || null,
      no_hp: noHp.trim() || null,
    };

    try {
      const res = await http.post('/penjualan', data, {
        headers: { Authorization: getAuthorizationHeader() },
      });
      setIsConfirmOpen(false);
      setIsOpen(true);
      playSound();
      setDataPrint(res.data.data);
      setNamaPembeli('');
      setNoHp('');
      return res.data;
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Data tidak ditemukan', { autoClose: 600 });
      } else if (error.response?.status === 422) {
        toast.error('Input Harga tidak boleh kosong', { autoClose: 600 });
      } else if (error.response?.status === 409) {
        toast.error('Silahkan scan qrcode', { autoClose: 600 });
      } else if (error.response?.status === 400) {
        toast.error(error.response.data?.message || 'Kesalahan validasi', { autoClose: 1500 });
      } else {
        toast.error('Terjadi kesalahan pada server');
      }
    } finally {
      setBtnLoading(false);
    }
  };

  const handleImageChange = (event) => {
    const selectedFiles = event.target.files;
    const selectedFilesArray = Array.from(selectedFiles);

    if (selectImage.length < 1) {
      const reader1 = new FileReader();
      reader1.addEventListener('load', function () {
        setGambar(reader1.result.split(',')[1]);
      });
      reader1.readAsDataURL(selectedFiles[0]);
    }
    const imagesArray = selectedFilesArray.map((file) => {
      return URL.createObjectURL(file);
    });
    setSelectImage(imagesArray);
    event.target.value = '';
  };

  const toko = localStorage.getItem('name');

  function deleteHandler(image) {
    setSelectImage(selectImage.filter((e) => e !== image));
    setGambar('');
    URL.revokeObjectURL(image);
  }

  const tipe_surat = localStorage.getItem('tipe_surat');
  const src = (activeItem?.gambar1 && activeItem.gambar1.length > 0) ? `${imageBaseUrl}${activeItem.gambar1[0]?.asli}` : '';

  return (
    <>

      <div className="w-full mt-2 pb-20">
        <div className="bg-white dark:bg-[#131b2e] shadow-md rounded-2xl p-6 sm:p-8 border border-slate-200 dark:border-slate-800">
          <h1 className='uppercase font-extrabold text-xl sm:text-2xl pb-3.5 border-b border-slate-200 dark:border-slate-800 text-slate-800 dark:text-slate-100 tracking-wide'>Penjualan {toko}</h1>
          <form onSubmit={handlePreSubmit} className="p-1">
            <div>
              {msg && (
                <div className="text-xl flex items-center justify-center font-semibold text-gray-700 dark:text-slate-300">{msg}</div>
              )}
            </div>
            <div className="w-0 h-0 overflow-hidden print:w-full print:h-auto print:overflow-visible">
              {tipe_surat === '1' ? (
                <div ref={strukComponent} className="fixed -left-[9999px] -top-[9999px] w-0 h-0 overflow-hidden invisible pointer-events-none print:visible print:static print:left-auto print:top-auto print:h-screen print:w-full print:overflow-visible print:pointer-events-auto bg-white">
                  <PrintSuratTrxPenjualan isOpen={isOpen} detailPenjualan={dataPrint} />
                </div>
              ) : tipe_surat === '2' ? (
                <div ref={strukComponent} className="fixed -left-[9999px] -top-[9999px] w-0 h-0 overflow-hidden invisible pointer-events-none print:visible print:static print:left-auto print:top-auto print:h-screen print:w-full print:overflow-visible print:pointer-events-auto bg-white">
                  <TestingPrint isOpen={isOpen} dataPrint={dataPrint} detailPenjualan={dataPrint} isTertunda={isTertunda} />
                </div>
              ) : tipe_surat === '3' ? (
                <div ref={strukComponent} className="fixed -left-[9999px] -top-[9999px] w-0 h-0 overflow-hidden invisible pointer-events-none print:visible print:static print:left-auto print:top-auto print:h-screen print:w-full print:overflow-visible print:pointer-events-auto bg-white">
                  <SuratSemuaToko isOpen={isOpen} dataPrint={dataPrint} detailPenjualan={dataPrint} isTertunda={isTertunda} />
                </div>
              ) : tipe_surat === '4' ? (
                <div ref={strukComponent} className="fixed -left-[9999px] -top-[9999px] w-0 h-0 overflow-hidden invisible pointer-events-none print:visible print:static print:left-auto print:top-auto print:h-screen print:w-full print:overflow-visible print:pointer-events-auto bg-white">
                  <SuratToko isOpen={isOpen} detailPenjualan={dataPrint} />
                </div>
              ) : null}
            </div>
              <div className="w-full">
                <div className="font-medium">
                  <center><div className="items-center p-3 mb-2 border-b dark:border-slate-800">
                    {!src ? '' :
                      <PhotoProvider>
                        <PhotoView src={src}>
                          <img className="items-center rounded-xl cursor-zoom-in shadow-sm hover:opacity-95 transition" width={'280px'} src={src}
                            alt="" />
                        </PhotoView>
                      </PhotoProvider>
                    }
                  </div></center>

                  <div className="flex justify-between items-center py-3.5 sm:py-4 px-2 border-b border-slate-200/80 dark:border-slate-800">
                    <div className='font-bold text-base sm:text-lg text-slate-700 dark:text-slate-200'>Kode Barang :</div>
                    <div className='font-extrabold text-base sm:text-lg text-amber-600 dark:text-amber-400 font-mono'>{loading ? 'memuat data...' : <>{activeItem?.kode_item || '-'}</>}</div>
                  </div>
                  <div className="flex justify-between items-center py-3.5 sm:py-4 px-2 border-b border-slate-200/80 dark:border-slate-800">
                    <div className='font-bold text-base sm:text-lg text-slate-700 dark:text-slate-200'>Nama Barang :</div>
                    <div className='font-extrabold text-base sm:text-lg text-amber-600 dark:text-amber-400'>{loading ? 'memuat data...' : <>{activeItem?.jenis_kategori?.nama_jenis || '-'}</>}</div>
                  </div>
                  <div className="flex justify-between items-center py-3.5 sm:py-4 px-2 border-b border-slate-200/80 dark:border-slate-800">
                    <div className='font-bold text-base sm:text-lg text-slate-700 dark:text-slate-200'>Berat :</div>
                    <div className='font-extrabold text-base sm:text-lg text-amber-600 dark:text-amber-400 font-mono'>{loading ? 'memuat data...' : <>{activeItem?.berat ? `${checkBerat(activeItem.berat)} gram` : '-'}</>}</div>
                  </div>
                  <div className="flex justify-between items-center py-3.5 sm:py-4 px-2 border-b border-slate-200/80 dark:border-slate-800">
                    <div className='font-bold text-base sm:text-lg text-slate-700 dark:text-slate-200'>Kadar :</div>
                    <div className='font-extrabold text-base sm:text-lg text-amber-600 dark:text-amber-400 font-mono'>{loading ? 'memuat data...' : <>{activeItem?.karat ? `${activeItem.karat} karat` : '-'}</>}</div>
                  </div>
                  <div className="flex justify-between items-center py-4 sm:py-5 px-2 border-b border-slate-200/80 dark:border-slate-800">
                    <div className='font-bold text-base sm:text-lg text-slate-700 dark:text-slate-200'>Total Harga :</div>
                    <div className="flex flex-col items-end">
                      <CurrencyInput
                        ref={hargaTotalRef}
                        id="input-harga-total"
                        value={hargaPenjualan}
                        onChange={handleChange}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handlePreSubmit(e);
                          }
                        }}
                        placeholder="Rp 0"
                        disabled={!hasValidItem}
                        className={`w-56 sm:w-72 h-12 sm:h-14 px-4 py-2 text-xl sm:text-2xl font-black text-right text-amber-600 dark:text-amber-400 bg-white dark:bg-slate-800 border-2 border-amber-400 dark:border-amber-500/70 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-500 placeholder:text-slate-400 dark:placeholder:text-slate-500 transition-all font-mono ${
                          !hasValidItem ? 'opacity-60 cursor-not-allowed bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-400 dark:text-slate-500' : ''
                        }`}
                      />
                    </div>
                  </div>
                  <div className="flex justify-between items-center py-4 px-2 border-b border-slate-200/80 dark:border-slate-800">
                    <div className='font-bold text-lg sm:text-xl text-slate-700 dark:text-slate-200'>Rata-rata Harga :</div>
                    <div className='font-black text-xl sm:text-2xl text-amber-600 dark:text-amber-400 font-mono'>
                        Rp. {numberWithCommas(activeItem?.berat && nominalHargaJual ? Math.round(nominalHargaJual / activeItem.berat) : 0)}
                    </div>
                  </div>
                </div>
              </div>
              <CetakStroke strukComponent={strukComponent} closeModal={closeModal} isOpen={isOpen} />

              {/* Modal Konfirmasi Final Sebelum Simpan Penjualan */}
              <ModalKonfirmasiPenjualan
                isOpen={isConfirmOpen}
                onClose={() => {
                  setIsConfirmOpen(false);
                  setTimeout(() => {
                    hargaTotalRef.current?.focus();
                  }, 100);
                }}
                onConfirm={handleConfirmSubmit}
                isSubmitting={btnLoading}
                item={activeItem}
                nominalHargaJual={nominalHargaJual}
                paymentMode={paymentMode}
                singleMethod={singleMethod}
                singleBank={singleBank}
                setSingleBank={setSingleBank}
                switchToSingle={switchToSingle}
                switchToSplit={switchToSplit}
                splitMethods={splitMethods}
                handleSplitChange={handleSplitChange}
                handleFillRemaining={handleFillRemaining}
                handleResetSplit={handleResetSplit}
                bankList={getEffectiveBankList(singleBank)}
                defaultBank={defaultBank}
                activePayments={activePayments}
                sisaSplit={sisaSplit}
                totalSplit={totalSplit}
                src={src}
                namaPembeli={namaPembeli}
                setNamaPembeli={setNamaPembeli}
                noHp={noHp}
                setNoHp={setNoHp}
                paymentMethods={paymentMethods}
              />

              {/* ================= TOMBOL SIMPAN PENJUALAN ================= */}
              <div className="mt-7 sm:mt-8 flex justify-end">
                <button
                  type="submit"
                  disabled={!hasValidItem || nominalHargaJual <= 0 || btnLoading}
                  className={`px-7 py-3.5 sm:py-4 rounded-xl font-extrabold text-base sm:text-lg transition flex items-center gap-2.5 ${
                    !hasValidItem || nominalHargaJual <= 0 || btnLoading
                      ? 'bg-slate-200 dark:bg-slate-800 text-slate-400 dark:text-slate-600 cursor-not-allowed'
                      : 'bg-emerald-600 hover:bg-emerald-700 text-white cursor-pointer shadow-lg shadow-emerald-600/30 active:scale-98'
                  }`}
                >
                  {btnLoading ? 'Memproses...' : 'Lanjut ke Pembayaran (Enter)'}
                </button>
              </div>
          </form>
        </div>
      </div>
    </>
  );
}
